import React from 'react';
import { Briefcase, Calendar, Globe, Mail, Phone, ChevronRight, Download } from 'lucide-react';
import { colors } from './constants';
import { formatGenre, calculateAge, getLatestExperience, calculateTotalExperienceYears } from './utils';

const ProfilePhoto = ({ demandeur }) => {
  if (demandeur.user && demandeur.user.photo) {
    return (
      <img 
        src={demandeur.user.photo} 
        alt={`${demandeur.user.first_name} ${demandeur.user.last_name}`} 
        className="h-full w-full object-cover rounded-full"
        onError={(e) => {
          e.target.onerror = null;
          e.target.style.display = 'none';
          e.target.parentElement.querySelector('.initials-fallback')?.classList.remove('hidden');
        }}
      />
    );
  }
  
  return null;
};

const InitialsFallback = ({ demandeur, hidden = false }) => (
  <div 
    className={`initials-fallback h-full w-full flex items-center justify-center text-white text-xl font-medium ${hidden ? 'hidden' : ''}`}
    style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` }}
  >
    {demandeur.user.first_name?.charAt(0) || ''}{demandeur.user.last_name?.charAt(0) || ''}
  </div>
);

const DemandeurCard = ({ demandeur, onOpenDetails, onExportCV }) => {
  const latestExperience = getLatestExperience(demandeur.experiences);

  return (
    <div 
      className="bg-white rounded-xl shadow-md overflow-hidden transition transform hover:shadow-lg hover:-translate-y-1 cursor-pointer"
      onClick={() => onOpenDetails(demandeur)}
    >
      {/* Badge de statut */}
      <div className="w-full h-2" style={{ backgroundColor: colors.primary }}></div>
      
      <div className="p-6">
        <div className="flex items-start">
          {/* Photo de profil */}
          <div className="flex-shrink-0">
            <div className="h-16 w-16 rounded-full overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` }}>
              <ProfilePhoto demandeur={demandeur} />
              <InitialsFallback demandeur={demandeur} hidden={!!demandeur.user?.photo} />
            </div>
          </div>
          
          {/* Informations principales */}
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {demandeur.profile ? formatGenre(demandeur.profile.genre) : ''} {demandeur.user.first_name} {demandeur.user.last_name}
            </h3>
            
            {latestExperience && (
              <div className="mt-1 flex items-center">
                <Briefcase size={14} className="text-orange-500 mr-1.5 flex-shrink-0" />
                <p className="text-gray-700 line-clamp-1">{latestExperience.poste}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Informations importantes */}
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="flex items-center px-2.5 py-1 bg-orange-50 text-orange-700 rounded-lg text-sm">
            <Calendar size={14} className="mr-1" />
            <span>{demandeur.profile ? calculateAge(demandeur.profile.date_naissance) : '-'} ans</span>
          </div>
          
          <div className="flex items-center px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-sm">
            <Briefcase size={14} className="mr-1" />
            <span>{calculateTotalExperienceYears(demandeur.experiences)} an(s) d'exp.</span>
          </div>
          
          {demandeur.langues.length > 0 && (
            <div className="flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <Globe size={14} className="mr-1" />
              <span>{demandeur.langues.length} langue(s)</span>
            </div>
          )}
        </div>
        
        {/* Informations de contact */}
        <div className="mt-3 grid grid-cols-1 gap-1">
          <div className="flex items-center text-gray-500 text-sm">
            <Mail className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-gray-400" />
            <span className="truncate">{demandeur.user.email}</span>
          </div>
          <div className="flex items-center text-gray-500 text-sm">
            <Phone className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-gray-400" />
            <span>{demandeur.user.telephone}</span>
          </div>
        </div>
        
        {/* Tags / Compétences */}
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500 mb-2">Compétences clés :</p>
          <div className="flex flex-wrap gap-1">
            {demandeur.competences.slice(0, 3).map((competence, index) => {
              const isOrange = index % 2 === 0;
              const bgColor = isOrange ? colors.primaryLight : colors.secondaryLight;
              const textColor = isOrange ? colors.primary : colors.secondary;
              
              return (
                <span 
                  key={competence.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-90"
                  style={{ backgroundColor: bgColor, color: textColor }}
                >
                  {competence.description}
                </span>
              );
            })}
            {demandeur.competences.length > 3 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors">
                +{demandeur.competences.length - 3}
              </span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="mt-3 flex justify-between items-center">
          <button 
            className="text-sm font-medium inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onExportCV(demandeur);
            }}
          >
            <Download className="h-4 w-4 mr-1.5" /> Exporter CV
          </button>
          <button 
            className="text-sm font-medium inline-flex items-center"
            style={{ color: colors.primary }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(demandeur);
            }}
          >
            Voir le profil <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemandeurCard;
